import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import LoadingBoundary from '~/src/components/LoadingBoundary';
import BaseTabs from '~/src/components/Tabs';
import { Urls } from '~/src/lib/enums/urls';
import { OteGame, isComplete, isLive } from '~/src/models/OteGame';
import { OteGameOtePlayerOteTeam } from '~/src/models/OteGameOtePlayerOteTeam';
import { OteGameOteTeam } from '~/src/models/OteGameOteTeam';
import { PlaylistItem } from '~/src/models/YouTube';
import useFetch from '~/src/overtime-lib/src/www/hooks/useFetch';
import BoxScore from './BoxScore';
import Events from './Events';
import Players from './Players';
import './Tabs.scss';
import Video from './Video';

const Tabs = ({
	game,
	players,
	homeGameTeam,
	awayGameTeam,
}: {
	game: OteGame;
	players: OteGameOtePlayerOteTeam[];
	homeGameTeam: OteGameOteTeam;
	awayGameTeam: OteGameOteTeam;
}) => {
	const { section } = useParams();

	const homeTeam = homeGameTeam?.ote_team;
	const awayTeam = awayGameTeam?.ote_team;

	// Check if YouTube video is ready and available to the public
	let youTubeVideo: PlaylistItem = null;
	if (game.youtube_id) {
		const fetchResult = useFetch<PlaylistItem>({
			url: `${Urls.ApiPlatformsYouTubeVideosUrl}/${game.youtube_id}`,
		});
		youTubeVideo = fetchResult.result.read();
	}

	const tabs: { name: string; text: string; component: JSX.Element }[] = [
		youTubeVideo?.snippet
			? { name: 'video', text: isLive(game) ? 'Live' : 'Watch', component: <Video youTubeVideo={youTubeVideo} /> }
			: null,
		!game.is_stats_hidden
			? {
					name: 'box_score',
					text: 'Box Score',
					component: <BoxScore game={game} players={players} awayGameTeam={awayGameTeam} homeGameTeam={homeGameTeam} />,
				}
			: null,
		!game.is_stats_hidden
			? {
					name: 'away',
					text: awayTeam?.name,
					component: (
						<Players players={players} ote_game={game} gameTeam={awayGameTeam} teamId={awayGameTeam?.ote_team_id} />
					),
				}
			: null,
		!game.is_stats_hidden
			? {
					name: 'home',
					text: homeTeam?.name,
					component: (
						<Players players={players} ote_game={game} gameTeam={homeGameTeam} teamId={homeGameTeam?.ote_team_id} />
					),
				}
			: null,
		!game.is_stats_hidden
			? isLive(game) || isComplete(game)
				? {
						name: 'play_by_play',
						text: 'Play By Play',
						component: <Events ote_game={game} awayGameTeam={awayGameTeam} homeGameTeam={homeGameTeam} />,
					}
				: null
			: null,
	].filter(Boolean);

	const defaultTab = section ? section : tabs[0]?.name;
	const [tabIndex, setTabIndex] = useState(tabs.findIndex((t) => t.name === defaultTab));
	const [tab, setTab] = useState(defaultTab);

	useEffect(() => {
		let index = tabs.map((t) => t.name).indexOf(tab);
		setTabIndex(index === -1 ? 0 : index);
	}, [tab]);

	return (
		<div className="GameTabs">
			<div className="TabBar">
				<div className="TabContainer">
					<BaseTabs
						links={tabs.map((t) => [t.name, t.text])}
						section={tab}
						useCallback={(t) => setTab(t)}
						root={`/games/${game.id}`}
						useHistoryState={true}
					/>
				</div>
			</div>

			<div className="TabContent">
				<div className="TransitionContainer">
					<div
						className="ContentGrid"
						style={{ left: `${-100 * tabIndex}%`, gridTemplateColumns: '100%'.repeat(tabs.length) }}
					>
						{tabs.map((t) => {
							return (
								<div key={t.name} style={{ maxHeight: tab === t.name ? null : 0 }}>
									<LoadingBoundary errorComponent={<></>}>{t.component}</LoadingBoundary>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Tabs;
